class MeridiaTheme {
  constructor() {
    this.header = document.querySelector('[data-site-header]');
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.bindDrawers();
    this.bindHeader();
    this.bindHero();
    this.bindAccordions();
    this.bindQuantities();
    this.bindVariants();
    this.bindGallery();
    this.bindAjaxCart();
    this.bindPredictiveSearch();
  }

  bindHeader() {
    if (!this.header) return;
    const update = () => this.header.classList.toggle('is-scrolled', window.scrollY > 24);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  bindDrawers() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-drawer]');
      const closer = event.target.closest('[data-close-drawer]');
      if (opener) this.openDrawer(opener.dataset.openDrawer);
      if (closer || event.target === this.overlay) this.closeDrawers();
    });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeDrawers(); });
  }

  openDrawer(id) {
    this.closeDrawers();
    const drawer = document.getElementById(id);
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('drawer-open');
    drawer.querySelector('button, a, input')?.focus();
  }

  closeDrawers() {
    document.querySelectorAll('.is-open[data-drawer]').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('drawer-open');
  }

  bindHero() {
    document.querySelectorAll('[data-hero]').forEach((hero) => {
      const slides = [...hero.querySelectorAll('[data-hero-slide]')];
      const dots = [...hero.querySelectorAll('[data-hero-dot]')];
      if (slides.length < 2) return;
      let index = 0;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); dot.setAttribute('aria-current', i === index ? 'true' : 'false'); });
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i)));
      if (hero.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setInterval(() => show(index + 1), Number(hero.dataset.speed) || 6500);
      }
    });
  }

  bindAccordions() {
    document.querySelectorAll('[data-accordion-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const section = button.closest('.footer-menu');
        const open = section.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
      });
    });
  }

  bindQuantities() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-change]');
      if (!button) return;
      const input = button.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(button.dataset.quantityChange);
      input.value = Math.max(Number(input.min) || 1, Number(input.value || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindVariants() {
    document.querySelectorAll('[data-product-form]').forEach((form) => {
      const data = form.querySelector('[data-product-json]');
      const idInput = form.querySelector('[name="id"]');
      if (!data || !idInput) return;
      const product = JSON.parse(data.textContent);
      const update = () => {
        const groups = [...form.querySelectorAll('[data-option-position]')];
        const options = [];
        groups.forEach((group, index) => {
          group.querySelectorAll('input').forEach((input) => {
            input.disabled = !product.variants.some((variant) => variant.available && variant.options[index] === input.value && options.every((value, prior) => variant.options[prior] === value));
          });
          let checked = group.querySelector('input:checked:not(:disabled)');
          if (!checked) {
            checked = group.querySelector('input:not(:disabled)');
            if (checked) checked.checked = true;
          }
          options.push(checked?.value);
        });
        const variant = groups.length ? product.variants.find((item) => item.options.every((option, i) => option === options[i])) : product.variants.find((item) => String(item.id) === String(idInput.value)) || product.variants[0];
        const button = form.querySelector('[data-add-to-cart]');
        if (variant) idInput.value = variant.id;
        button.disabled = !variant?.available;
        button.textContent = variant?.available ? button.dataset.addLabel : (variant ? button.dataset.soldLabel : button.dataset.unavailableLabel);
        form.querySelectorAll('[data-sticky-id]').forEach((input) => input.value = variant?.id || '');
      };
      form.addEventListener('change', update);
      update();
    });
  }

  bindGallery() {
    document.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const gallery = thumb.closest('[data-product-gallery]');
        const image = gallery.querySelector('[data-gallery-main]');
        image.src = thumb.dataset.image;
        image.srcset = '';
        image.alt = thumb.dataset.alt || '';
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.toggle('is-active', item === thumb));
      });
    });
  }

  bindAjaxCart() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form || !window.fetch) return;
      if (!event.submitter?.matches('[data-add-to-cart]')) return;
      event.preventDefault();
      const button = event.submitter || form.querySelector('[data-add-to-cart]');
      button?.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error((await response.json()).description || 'Cart error');
        await this.refreshCart();
        this.openDrawer('CartDrawer');
      } catch (error) { alert(error.message); }
      finally { button?.removeAttribute('aria-busy'); }
    });
  }

  async refreshCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((el) => el.textContent = cart.item_count);
    document.querySelectorAll('[data-cart-total]').forEach((el) => el.textContent = new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: cart.currency }).format(cart.total_price / 100));
    const body = document.querySelector('[data-cart-drawer-body]');
    if (!body) return;
    body.innerHTML = cart.items.length ? cart.items.map((item) => `<div class="cart-drawer__item"><img src="${item.image || ''}" alt=""><div><a href="${item.url}"><strong>${this.escape(item.product_title)}</strong></a><div>${this.escape(item.variant_title || '')}</div><small>${item.quantity} × ${this.money(item.final_price, cart.currency)}</small></div></div>`).join('') : '<p>Your cart is empty.</p>';
  }

  money(value, currency) { return new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency }).format(value / 100); }
  escape(value) { const div = document.createElement('div'); div.textContent = value; return div.innerHTML; }

  bindPredictiveSearch() {
    document.querySelectorAll('[data-predictive-input]').forEach((input) => {
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(async () => {
          const target = document.querySelector(input.dataset.results);
          if (!target || input.value.trim().length < 2) { if (target) target.innerHTML = ''; return; }
          const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(input.value)}&resources[type]=product&resources[limit]=6`;
          const json = await (await fetch(url)).json();
          const products = json.resources.results.products || [];
          target.innerHTML = products.map((p) => `<a class="cart-drawer__item" href="${p.url}">${p.featured_image?.url ? `<img src="${p.featured_image.url}" alt="">` : ''}<strong>${this.escape(p.title)}</strong></a>`).join('');
        }, 220);
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new MeridiaTheme());
